import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Book, Monitor } from 'react-feather'
import { faColumns3, faCube, faPaperPlane, faSignsPost } from '@fortawesome/pro-regular-svg-icons'
import { homeDropdownOptions, isIntermediaries, isSupplier } from '@src/utility/Utils'
import { getUserData } from '@src/auth/utils'
import { Button, ChoiceCard, Dialog, DropdownButton } from '@src/views/components/ds'
import './style.scss'

const TRIP_PIPELINE_PATH = '/Pipeline/Trips'
// The Engage pipeline opens its create trip drawer when its URL carries this query.
const CREATE_TRIP_PATH = `${TRIP_PIPELINE_PATH}?create=trip`

const DROPDOWN_ICONS = {
    Itinerary: faPaperPlane,
    Tour: faSignsPost,
    Component: faCube
}

const Dashboard = () => {
    const userData = getUserData()
    const history = useHistory()
    const abilities = useSelector(state => state.auth.abilities)

    const { DayTours } = JSON.parse(userData?.AccountOptions)

    const isIntermediary = isIntermediaries(userData.AccountType, userData.AccountPlan, DayTours)
    const isDayTourUser = ["Day Tour"].includes(userData.AccountPlan) || ((["TravelAgent"].includes(userData.AccountType) || ["Content", "Special"].includes(userData.AccountPlan)) && DayTours === "Enabled")
    // Follows the same tier rule as the Trip pipeline menu item and route.
    const canViewPipeline = !!abilities?.can('read', 'trip-pipeline')

    const [createOption, setCreateOption] = useState(null)

    useEffect(() => {
        if (window.Beacon && !window.BeaconInitialized) {
            window.Beacon("identify", {
                name: userData.Username,
                email: userData.UserEmail ? userData.UserEmail : (userData.AccountEmail ? userData.AccountEmail : ""),
                company: userData.CompanyName,
                type: userData.AccountType,
                tier: userData.AccountPlan,
                admin: `https://${process.env.REACT_APP_MONOLITH_URL}/Admin/Operators/Edit/${userData.AccountId}`
            })

            window.Beacon('config', {
                color: "#6cb741",
                display: {
                    style: "manual"
                }
            })

            window.Beacon('init', '8af56432-b17a-4171-9dd6-b3416111ca0a')
            window.BeaconInitialized = true
        }
    }, [])

    const createTrip = () => history.push(CREATE_TRIP_PATH)

    const builderOptions = homeDropdownOptions(userData.AccountType, userData.AccountPlan, DayTours)

    const dropdownItems = [
        ...(canViewPipeline ? [
            {
                id: 'Trip',
                elementId: 'dropdown-trip',
                label: 'Trip',
                description: 'Track the admin and next steps of every journey.',
                icon: faColumns3,
                onClick: createTrip
            }
        ] : []),
        ...builderOptions.map((option) => ({
            id: option.id,
            elementId: `dropdown-${option.id.toLowerCase()}`,
            label: option.label,
            icon: DROPDOWN_ICONS[option.id],
            onClick: () => setCreateOption(option)
        }))
    ]

    // Without the pipeline the button keeps its old default: a tour for day tour accounts, otherwise an itinerary.
    const defaultBuilderOption = builderOptions.find((option) => option.id === (isDayTourUser ? 'Tour' : 'Itinerary')) || builderOptions[0]
    const defaultAction = canViewPipeline ? { label: 'Create trip', onClick: createTrip } : { label: defaultBuilderOption?.defaultValue, onClick: () => setCreateOption(defaultBuilderOption) }

    const closeCreateDialog = () => setCreateOption(null)

    const openBuilder = (url) => {
        closeCreateDialog()
        history.push(url)
    }

    const renderHeroContent = () => {
        if (isIntermediary) {
            return (
                <>
                    <h1 className="fw-medium">Keep every journey moving forward.</h1>
                    <h4 className="fw-medium">Pick up where you left off or start planning a new trip.</h4>

                    <div className="hero-card-actions d-flex flex-wrap">
                        {canViewPipeline && (
                            <Button id="open-trip-pipeline" buttonType="secondary" icon={faColumns3} onClick={() => history.push(TRIP_PIPELINE_PATH)}>
                                Trip pipeline
                            </Button>
                        )}

                        {isDayTourUser ? (
                            <Button id="open-day-tours" buttonType="outline" icon={faSignsPost} onClick={() => history.push('/ItineraryBuilder/DayTours')}>
                                Day tours
                            </Button>
                        ) : ["TravelAgent"].includes(userData.AccountType) && DayTours === "Disabled" ? (
                            null
                        ) : (
                            <Button id="open-personal-itineraries" buttonType="outline" icon={faPaperPlane} onClick={() => history.push('/ItineraryBuilder/Personal')}>
                                Personal itineraries
                            </Button>
                        )}
                    </div>
                </>
            )
        }

        if (isSupplier(userData.AccountType, userData.AccountPlan, DayTours)) {
            return (
                <>
                    <h1 className="fw-medium">Maintain high-quality, up-to-date content.</h1>
                    <h4 className="fw-medium">Create product Profiles that are accessible to trade professionals.</h4>
                </>
            )
        }

        return (
            <>
                <h1 className="fw-medium">Welcome to Wetu</h1>
                <h4 className="fw-medium">Make buying travel a first-class experience.</h4>
            </>
        )
    }

    return (
        <>
            <Dialog
                isOpen={!!createOption}
                onClose={closeCreateDialog}
                size="lg"
                labelledBy="create-dialog-title"
                header={(
                    <>
                        <h4 id="create-dialog-title">{createOption?.heading}</h4>
                        <p>{createOption?.subparagraph}</p>
                    </>
                )}
                body={(
                    <div className="create-dialog-options">
                        {createOption?.subOptions.map((subOption) => (
                            <ChoiceCard
                                key={subOption.id}
                                id={subOption.id}
                                icon={subOption.icon}
                                heading={subOption.title}
                                body={subOption.paragraph}
                                onClick={() => openBuilder(subOption.url)}
                            />
                        ))}
                    </div>
                )}
            />

            <div className="bg-white d-flex flex-column vh-100">
                <div className="header-container">
                    <div className="header-inner-container d-flex justify-content-between align-items-center shadow">
                        <h2 className="fw-medium mb-0">Home</h2>

                        {isIntermediary && dropdownItems.length > 0 && (
                            <DropdownButton
                                id="dashboard-dropdown-button"
                                label={defaultAction.label}
                                onClick={defaultAction.onClick}
                                items={dropdownItems}
                            />
                        )}
                    </div>
                </div>

                <div className="hero-container d-flex flex-column flex-grow-1">
                    <div className="flex-grow-1">
                        {/* Userpilot embeds its content after #usp-home-hero, inside this row, so the card stretches to share the width. */}
                        <div className="hero-row px-1 d-flex">
                            <div id="usp-home-hero" className="hero-card-container hero-card d-flex flex-column justify-content-center shadow">
                                {renderHeroContent()}
                            </div>
                        </div>

                        <div className="hero-bottom-container px-1 d-flex">
                            <div className="hero-card-container flex-basis-[1/3] d-flex flex-column justify-content-center shadow">
                                <h4 className="text-nowrap">Explore what we've been up to.</h4>
                                <div className="hero-card-container-sub-heading d-flex">
                                    <Monitor size={18} />
                                    <p className="ps-[4px] mb-0">Wetu blog</p>
                                </div>
                                <p className="hero-card-container-paragraph">Read about out latest updates and industry insights, and more.</p>
                                <a className="d-inline-block text-primary fs-6" target='_blank' href="https://blog.wetu.com/?utm_source=WetuDashboard&utm_id=Platform">See all articles <i className="ml-[10px] feather icon-arrow-right"></i></a>
                            </div>

                            <div className="hero-card-container flex-basis-[2/3] d-flex flex-column justify-content-center shadow">
                                <h4>Need some help?</h4>
                                <div className="hero-card-container-gap d-flex justify-content-between">
                                    <div>
                                        <div className="hero-card-container-sub-heading d-flex">
                                            <Book size={18} />
                                            <p className="ps-[4px] mb-0">Knowledge base</p>
                                        </div>
                                        <p className="hero-card-container-paragraph">Find all the information you need in our documentation.</p>
                                        <a className="d-inline-block text-primary fs-6" target='_blank' href="https://knowledge.wetu.com/">Visit the Knowledge Base <i className="ml-[10px] feather icon-arrow-right"></i></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hero-container-footer">
                        <h6 className="text-center mb-0">Trusted by thousands of travel specialists</h6>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Dashboard
